import { Suspense, useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useAuthStatus } from "@/contexts/OptimizedAuthContext";
import { useResponsive } from "@/contexts/ResponsiveContext";
import { NavigationRibbon } from "@/components/navigation/NavigationRibbon";
import { MobileBottomNav } from "@/components/navigation/mobile/MobileBottomNav";
import { MobileMenuSheet } from "@/components/navigation/mobile/MobileMenuSheet";
import { ShellContext } from "./shell-context";

/**
 * The authenticated app shell. Desktop/tablet: persistent NavigationRibbon on
 * the left, routed page content on the right. Mobile: full-viewport column
 * with a floating bottom dock (MobileBottomNav) and menu sheet instead of the
 * ribbon. Used as a react-router layout route wrapping the signed-in
 * destinations. Bare surfaces (/auth, /share, /api-docs) stay outside it.
 */
export function AppShell() {
  const { isSignedIn, isLoaded } = useAuthStatus();
  const { isMobile } = useResponsive();
  const [menuOpen, setMenuOpen] = useState(false);

  // Close the sheet whenever we leave mobile (rotate to desktop width).
  useEffect(() => {
    if (!isMobile) setMenuOpen(false);
  }, [isMobile]);

  // The dock sits above the sheet's backdrop, so users can navigate while the
  // sheet is open — close it on any route change.
  const { pathname } = useLocation();
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  // Pin the document while the mobile shell is mounted — iOS Safari
  // rubber-banding can't be fully suppressed with overflow:hidden alone;
  // the .mobile-shell-locked rules in index.css position:fix the body.
  useEffect(() => {
    if (!isMobile) return;
    document.documentElement.classList.add("mobile-shell-locked");
    document.body.classList.add("mobile-shell-locked");
    return () => {
      document.documentElement.classList.remove("mobile-shell-locked");
      document.body.classList.remove("mobile-shell-locked");
    };
  }, [isMobile]);

  if (isMobile) {
    return (
      <ShellContext.Provider value={{ inShell: true }}>
        <div
          // fixed + 100dvh anchors to the iOS visual viewport and tracks
          // URL-bar shrink/grow. `isolate` opens a fresh stacking context so
          // no z-index inside routed content can ever paint over the dock —
          // the dock and sheet are `absolute` siblings layered above the
          // scroll container within this context, not `fixed` against the
          // window (which a transformed/filtered ancestor can silently
          // re-anchor). --mobile-bottom-clearance is the single source of
          // truth for how much bottom padding keeps content clear of the
          // floating dock (dock ~60px + 12px offset + gap).
          className="fixed inset-0 isolate flex flex-col overflow-hidden bg-background text-foreground"
          style={
            {
              height: "100dvh",
              overscrollBehavior: "none",
              "--mobile-bottom-clearance": "calc(env(safe-area-inset-bottom, 0px) + 84px)",
            } as React.CSSProperties
          }
        >
          <div
            className="relative z-0 flex-1 min-w-0 overflow-y-auto"
            style={{
              paddingBottom: "var(--mobile-bottom-clearance)",
              scrollPaddingBottom: "var(--mobile-bottom-clearance)",
              WebkitOverflowScrolling: "touch",
              overscrollBehavior: "contain",
            }}
          >
            <Suspense fallback={<div>Loading...</div>}>
              <Outlet />
            </Suspense>
          </div>
          {isLoaded && isSignedIn && (
            <>
              <MobileBottomNav menuOpen={menuOpen} onToggleMenu={() => setMenuOpen((v) => !v)} />
              <MobileMenuSheet open={menuOpen} onClose={() => setMenuOpen(false)} />
            </>
          )}
        </div>
      </ShellContext.Provider>
    );
  }

  return (
    <ShellContext.Provider value={{ inShell: true }}>
      <div className="flex h-screen overflow-hidden bg-background text-foreground">
        {isLoaded && isSignedIn && <NavigationRibbon />}
        <div className="flex-1 min-w-0 overflow-y-auto">
          <Suspense fallback={<div>Loading...</div>}>
            <Outlet />
          </Suspense>
        </div>
      </div>
    </ShellContext.Provider>
  );
}
